/**
 * Shopify export target.
 *
 * The 69 Shopify product-export column headers, in the exact order used by
 * Monjolika's live store CSV (see sample-data/products_export (1).csv).
 *
 * This is the single source of truth for the Shopify path. `columns.ts`
 * re-exports these as `SHOPIFY_COLUMNS`/`COLUMN_GROUPS`/etc. for backward
 * compatibility with existing imports. Do not reorder or rename without
 * re-running the golden-file CSV test.
 */

import type { ExportTarget } from "./types";

const COLUMNS = [
  "Handle",
  "Title",
  "Body (HTML)",
  "Vendor",
  "Product Category",
  "Type",
  "Tags",
  "Published",
  "Option1 Name",
  "Option1 Value",
  "Option1 Linked To",
  "Option2 Name",
  "Option2 Value",
  "Option2 Linked To",
  "Option3 Name",
  "Option3 Value",
  "Option3 Linked To",
  "Variant SKU",
  "Variant Grams",
  "Variant Inventory Tracker",
  "Variant Inventory Qty",
  "Variant Inventory Policy",
  "Variant Fulfillment Service",
  "Variant Price",
  "Variant Compare At Price",
  "Variant Requires Shipping",
  "Variant Taxable",
  "Unit Price Total Measure",
  "Unit Price Total Measure Unit",
  "Unit Price Base Measure",
  "Unit Price Base Measure Unit",
  "Variant Barcode",
  "Image Src",
  "Image Position",
  "Image Alt Text",
  "Gift Card",
  "SEO Title",
  "SEO Description",
  "Google Shopping / Google Product Category",
  "Google Shopping / Gender",
  "Google Shopping / Age Group",
  "Google Shopping / MPN",
  "Google Shopping / Condition",
  "Google Shopping / Custom Product",
  "Google Shopping / Custom Label 0",
  "Google Shopping / Custom Label 1",
  "Google Shopping / Custom Label 2",
  "Google Shopping / Custom Label 3",
  "Google Shopping / Custom Label 4",
  "More colors (product.metafields.custom.more_colors)",
  "Pack contains (product.metafields.custom.pack_contains)",
  "Product Specification  (product.metafields.custom.product_specification)",
  "Short Description (product.metafields.custom.short_description)",
  " Use variants image group (product.metafields.custom.use_variants_image_group)",
  "Google: Custom Product (product.metafields.mm-google-shopping.custom_product)",
  "Age group (product.metafields.shopify.age-group)",
  "Color (product.metafields.shopify.color-pattern)",
  "Fabric (product.metafields.shopify.fabric)",
  "Size (product.metafields.shopify.size)",
  "Target gender (product.metafields.shopify.target-gender)",
  "Complementary products (product.metafields.shopify--discovery--product_recommendation.complementary_products)",
  "Related products (product.metafields.shopify--discovery--product_recommendation.related_products)",
  "Related products settings (product.metafields.shopify--discovery--product_recommendation.related_products_display)",
  "Search product boosts (product.metafields.shopify--discovery--product_search_boost.queries)",
  "Variant Image",
  "Variant Weight Unit",
  "Variant Tax Code",
  "Cost per item",
  "Status",
] as const;

const COLUMN_GROUPS = [
  {
    label: "Core",
    columns: [
      "Handle",
      "Title",
      "Body (HTML)",
      "Vendor",
      "Product Category",
      "Type",
      "Tags",
      "Published",
      "Status",
      "Gift Card",
    ],
  },
  {
    label: "Variants",
    columns: [
      "Option1 Name",
      "Option1 Value",
      "Option1 Linked To",
      "Option2 Name",
      "Option2 Value",
      "Option2 Linked To",
      "Option3 Name",
      "Option3 Value",
      "Option3 Linked To",
      "Variant SKU",
      "Variant Grams",
      "Variant Inventory Tracker",
      "Variant Inventory Qty",
      "Variant Inventory Policy",
      "Variant Fulfillment Service",
      "Variant Price",
      "Variant Compare At Price",
      "Variant Requires Shipping",
      "Variant Taxable",
      "Variant Barcode",
      "Variant Image",
      "Variant Weight Unit",
      "Variant Tax Code",
      "Cost per item",
      "Unit Price Total Measure",
      "Unit Price Total Measure Unit",
      "Unit Price Base Measure",
      "Unit Price Base Measure Unit",
    ],
  },
  {
    label: "Images",
    columns: ["Image Src", "Image Position", "Image Alt Text"],
  },
  {
    label: "SEO",
    columns: ["SEO Title", "SEO Description"],
  },
  {
    label: "Google",
    columns: [
      "Google Shopping / Google Product Category",
      "Google Shopping / Gender",
      "Google Shopping / Age Group",
      "Google Shopping / MPN",
      "Google Shopping / Condition",
      "Google Shopping / Custom Product",
      "Google Shopping / Custom Label 0",
      "Google Shopping / Custom Label 1",
      "Google Shopping / Custom Label 2",
      "Google Shopping / Custom Label 3",
      "Google Shopping / Custom Label 4",
      "Google: Custom Product (product.metafields.mm-google-shopping.custom_product)",
    ],
  },
  {
    label: "Metafields",
    columns: [
      "More colors (product.metafields.custom.more_colors)",
      "Pack contains (product.metafields.custom.pack_contains)",
      "Product Specification  (product.metafields.custom.product_specification)",
      "Short Description (product.metafields.custom.short_description)",
      " Use variants image group (product.metafields.custom.use_variants_image_group)",
      "Age group (product.metafields.shopify.age-group)",
      "Color (product.metafields.shopify.color-pattern)",
      "Fabric (product.metafields.shopify.fabric)",
      "Size (product.metafields.shopify.size)",
      "Target gender (product.metafields.shopify.target-gender)",
      "Complementary products (product.metafields.shopify--discovery--product_recommendation.complementary_products)",
      "Related products (product.metafields.shopify--discovery--product_recommendation.related_products)",
      "Related products settings (product.metafields.shopify--discovery--product_recommendation.related_products_display)",
      "Search product boosts (product.metafields.shopify--discovery--product_search_boost.queries)",
    ],
  },
] as const;

/**
 * Shopify columns that are typically left blank on Monjolika's live store CSV.
 *
 * Verified against sample-data/products_export (1).csv: each entry is empty
 * across all 5 sample data rows of that real Shopify export. The mapper UI
 * uses this list to (a) hide these columns by default and (b) flag them with
 * an "N/A" legend pill so the user can still surface them if needed.
 */
const NA_BY_DEFAULT = [
  "Option1 Linked To",
  "Option2 Name",
  "Option2 Value",
  "Option2 Linked To",
  "Option3 Name",
  "Option3 Value",
  "Option3 Linked To",
  "Unit Price Total Measure",
  "Unit Price Total Measure Unit",
  "Unit Price Base Measure",
  "Unit Price Base Measure Unit",
  "Variant Barcode",
  "SEO Title",
  "SEO Description",
  "Google Shopping / Gender",
  "Google Shopping / Age Group",
  "Google Shopping / MPN",
  "Google Shopping / Condition",
  "Google Shopping / Custom Product",
  "Google Shopping / Custom Label 0",
  "Google Shopping / Custom Label 1",
  "Google Shopping / Custom Label 2",
  "Google Shopping / Custom Label 3",
  "Google Shopping / Custom Label 4",
  "More colors (product.metafields.custom.more_colors)",
  "Short Description (product.metafields.custom.short_description)",
  " Use variants image group (product.metafields.custom.use_variants_image_group)",
  "Google: Custom Product (product.metafields.mm-google-shopping.custom_product)",
  "Age group (product.metafields.shopify.age-group)",
  "Size (product.metafields.shopify.size)",
  "Complementary products (product.metafields.shopify--discovery--product_recommendation.complementary_products)",
  "Related products (product.metafields.shopify--discovery--product_recommendation.related_products)",
  "Related products settings (product.metafields.shopify--discovery--product_recommendation.related_products_display)",
  "Search product boosts (product.metafields.shopify--discovery--product_search_boost.queries)",
  "Variant Image",
  "Variant Tax Code",
  "Cost per item",
] as const;

export const shopifyTarget: ExportTarget = {
  id: "shopify",
  name: "Shopify (multi-row CSV)",
  columns: COLUMNS,
  columnGroups: COLUMN_GROUPS,
  naByDefault: NA_BY_DEFAULT,
  priceLikeColumns: ["Variant Price", "Variant Compare At Price"],
  skuLikeColumns: ["Variant SKU"],
  variantSchema: { kind: "shopify-option-trio" },
  fanout: {
    kind: "shopify-multi-row",
    handleColumn: "Handle",
    imageColumns: {
      src: "Image Src",
      position: "Image Position",
      alt: "Image Alt Text",
    },
  },
  delimiter: ",",
  rowEnding: "\r\n",
};
